import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { ArticleRepositoryPort } from 'article/port/article-repository.port';
import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { ModuleGraphRepositoryPort } from 'module/page/graph/port/module-graph-repository.port';
import { ModulePageType } from 'module/page/model/module-page.type';
import { ModulePage } from 'module/page/module-page';
import { ModulePageI18n } from 'module/page/module-page-i18n';
import { ModuleQuestionRepositoryPort } from 'module/page/question/port/module-question-repository.port';
import { ModuleTaskRepositoryPort } from 'module/page/task/port/module-task-repository.port';

import { AppLocator } from '../../../app.locator';

export interface ModulePageI18nDocument {
	readonly title: string;
	readonly locale: string;
	readonly text: string;
	readonly link: string;
}

export interface ModulePageDocument {
	readonly id: string;
	readonly type: string;
	readonly position: number;
	readonly tasksId: string[];
	readonly graphsId: string[];
	readonly articlesId: string[];
	readonly documentsId: string[];
	readonly questionsId: string[];
	readonly updateTimestamp: number;
	readonly localizations: ModulePageI18nDocument[];
}

export type ModulePageState = EntityState<ModulePageDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'modulePages' })
export class ModulePageStore extends EntityStore<ModulePageState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public get _moduleGraphRepositoryPort(): ModuleGraphRepositoryPort {
		return AppLocator.inject<ModuleGraphRepositoryPort>(
			ModuleGraphRepositoryPort
		);
	}

	public get _moduleTaskRepositoryPort(): ModuleTaskRepositoryPort {
		return AppLocator.inject<ModuleTaskRepositoryPort>(ModuleTaskRepositoryPort);
	}

	public get _documentRepositoryPort(): DocumentRepositoryPort {
		return AppLocator.inject<DocumentRepositoryPort>(DocumentRepositoryPort);
	}

	public get _articleRepositoryPort(): ArticleRepositoryPort {
		return AppLocator.inject<ArticleRepositoryPort>(ArticleRepositoryPort);
	}

	public get _moduleQuestionRepositoryPort(): ModuleQuestionRepositoryPort {
		return AppLocator.inject<ModuleQuestionRepositoryPort>(
			ModuleQuestionRepositoryPort
		);
	}

	public asDocument(page: ModulePage): ModulePageDocument {
		return page
			? {
					id: page.id,
					type: page.type,
					tasksId: page.tasksId,
					graphsId: page.graphsId,
					position: page.position,
					articlesId: page.articlesId,
					documentsId: page.documentsId,
					questionsId: page.questionsId,
					updateTimestamp: page.updateTimestamp,
					localizations: page.localizations.map((loc) => ({
						text: loc.text,
						title: loc.title,
						locale: loc.locale,
						link: loc.link,
					})),
			  }
			: null;
	}

	public asEntity(document: ModulePageDocument): ModulePage {
		const localizations = !!document
			? document.localizations.map(
					(loc) => new ModulePageI18n(loc.title, loc.locale, loc.text, loc.link)
			  )
			: [];

		return document
			? new ModulePage(
					document.id,
					document.position,
					ModulePageType[document.type],
					document.updateTimestamp,
					localizations,
					document.tasksId,
					document.graphsId,
					document.articlesId,
					document.questionsId,
					document.documentsId,
					document.tasksId
						? this._moduleTaskRepositoryPort.observeModuleTasksByIds(document.tasksId)
						: of([]),
					document.articlesId
						? this._articleRepositoryPort.observeArticlesByIds(document.articlesId)
						: of([]),
					document.graphsId
						? this._moduleGraphRepositoryPort.observeModuleGraphsByIds(
								document.graphsId
						  )
						: of([]),
					document.questionsId
						? this._moduleQuestionRepositoryPort.observeModuleQuestionsByIds(
								document.questionsId
						  )
						: of([]),
					document.documentsId
						? this._documentRepositoryPort.observeDocuments(document.documentsId)
						: of([]),
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
